import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import SelectBox, {Props} from 'react-select';
import {useOrientation} from '../../hooks/orientation';
import {useLocalization} from "../../hooks/localization";

const MAX_HEIGHT = 250;
const ITEM_HEIGHT = 40;

// noinspection JSUnusedGlobalSymbols
const selectStyles = (isUp: boolean) => ({
    control: (provided, {menuIsOpen}) => ({
        height: ITEM_HEIGHT,
        width: 250,
        color: '#efdd1c',
        borderRadius: menuIsOpen ? (isUp ? '0 0 10px 10px' : '10px 10px 0 0') : 10,
        border: '1px solid #6565657f',
        backgroundColor: '#00000065',
        fontSize: 20,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 10px',
    }),
    singleValue: () => ({
        maxWidth: 170,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    }),
    indicatorSeparator: () => ({}),
    indicatorsContainer: () => ({color: 'white'}),
    option: (_, state) => {
        return ({
            height: ITEM_HEIGHT,
            width: 250,
            color: state.isSelected ? '#303030' : '#efdd1c',
            borderBottom: state.value === state.options[state.options.length - 1].value ? 0 : '1px solid #6565657f',
            borderTop: state.value === state.options[0].value ? 0 : '1px solid #6565657f',
            backgroundColor: state.isSelected ? '#efdd1c' : '#00000065',
            fontSize: 20,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 10px',
        });
    },
    menu: (provided) => ({
        ...provided,
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        backgroundColor: '#303030',
        ...isUp ? {
            top: 'auto',
            bottom: '100%',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
        } : {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
        },
        border: '1px solid #6565657f',
        borderTop: 0,
    }),
    menuList: () => ({
        maxHeight: MAX_HEIGHT,
        overflow: 'scroll',
    }),
    multiValue: () => ({
        marginRight: '5px',
    }),
    multiValueLabel: () => ({}),
    multiValueRemove: () => ({display: 'none'}),
    container: (provided) => ({...provided, height: 40}),
    valueContainer: (provided) => ({...provided, overflow: 'hidden', flexWrap: 'nowrap'}),
});

export type SelectItem<T> = {
    label: string;
    value: T;
};

export function Select<OptionType>(props: Props<OptionType>) {
    const {t} = useLocalization();
    const orientation = useOrientation();
    const ref = useRef<SelectBox>();
    const [isUp, setIsUp] = useState(false);

    const styles = useMemo(() => selectStyles(isUp), [isUp]);

    const determineUp = useCallback(() => {
        if (!ref.current) {
            return;
        }

        if (!ref.current.select) {
            return;
        }

        // @ts-ignore
        const inputRef = ref.current.select.inputRef as HTMLInputElement;

        if (!inputRef) {
            return;
        }

        let windowHeight = window.innerHeight;
        if (orientation === 'none') {
            if (windowHeight > 600) {
                windowHeight /= 2;
                windowHeight += 300;
            }
        }

        const options = props.options || [];
        const height = Math.min(MAX_HEIGHT, options.length * ITEM_HEIGHT);
        const offset = inputRef.getBoundingClientRect().bottom + height;
        // console.log({offset, windowHeight});

        setIsUp(offset > windowHeight);
    }, [orientation, props.options]);

    useEffect(() => {
        window.addEventListener('resize', determineUp);
        window.addEventListener('scroll', determineUp);

        return () => {
            window.removeEventListener('resize', determineUp);
            window.removeEventListener('scroll', determineUp);
        };
    }, [determineUp]);

    useEffect(determineUp, [props, orientation]);
    return (
        // @ts-ignore
        <SelectBox
            ref={ref}
            placeholder={t('select')}
            noOptionsMessage={() => t('empty_list')}
            closeMenuOnSelect={!props.isMulti}
            isClearable={false}
            hideSelectedOptions={false}
            {...props}
            styles={styles}
        />
    );
}
